import React from 'react'
import * as style from 'src/app/BulkInvite/components/Heading.style'

type HeadingVariant = 'page' | 'section'

const VARIANT_STYLE = {
  page: style.page,
  section: style.section,
} as const

const DEFAULT_LEVEL: Record<HeadingVariant, 1 | 2> = {
  page: 1,
  section: 2,
}

interface HeadingProps {
  variant?: HeadingVariant
  /** Override the semantic heading level (defaults from variant). */
  ariaLevel?: 1 | 2 | 3
  className?: string
  children: React.ReactNode
}

/**
 * Native semantic heading (<h1>/<h2>/<h3>) with the UA bold weight reset so our
 * Rubik-Medium face renders as designed. `variant` controls the visual size;
 * `ariaLevel` controls the actual tag so the heading slots into the page
 * outline correctly.
 */
const Heading: React.FC<HeadingProps> = ({
  variant = 'section',
  ariaLevel,
  className,
  children,
}) => {
  const level = ariaLevel ?? DEFAULT_LEVEL[variant]
  const headingCss = VARIANT_STYLE[variant]
  // Render static tags (not a dynamic <Tag>) — the styled-components `css` prop
  // transform can't hoist a styled wrapper for a runtime-variable element, which
  // crashes at runtime ("_$Tag is not defined").
  if (level === 1) {
    return (
      <h1 className={className} css={headingCss}>
        {children}
      </h1>
    )
  }
  if (level === 3) {
    return (
      <h3 className={className} css={headingCss}>
        {children}
      </h3>
    )
  }
  return (
    <h2 className={className} css={headingCss}>
      {children}
    </h2>
  )
}

export default Heading
